import {atom} from 'nanostores';
import {
  isWebView2Host,
  onHostMessage,
  postToHost,
} from '../utils/host/webviewHost';

/** The ResWare file the desktop client currently has open, mirrored from the host. */
export interface ActiveFile {
  fileId: number;
  fileNumber: string | null;
}

/**
 * The active ResWare file, or null when none is in context. Inside the desktop client it is
 * mirrored from the host (null on the home page); in a regular browser it comes from the
 * `?fileId=` URL parameter, or stays null when that is absent. The AI Assistant and its features
 * read this to default to the file the user is on.
 */
export const $activeFile = atom<ActiveFile | null>(null);

// Message protocol shared with the desktop host (see ClientWinforms FileContextWebViewExtension).
const FILE_CONTEXT_MESSAGE_TYPE = 'reswareFileContext';
const REQUEST_FILE_CONTEXT_MESSAGE_TYPE = 'requestFileContext';

interface FileContextMessage {
  type: typeof FILE_CONTEXT_MESSAGE_TYPE;
  fileId: number | null;
  fileNumber: string | null;
}

function isFileContextMessage(data: unknown): data is FileContextMessage {
  if (typeof data !== 'object' || data === null) return false;
  const d = data as Record<string, unknown>;
  return (
    d.type === FILE_CONTEXT_MESSAGE_TYPE &&
    (d.fileId === null || typeof d.fileId === 'number') &&
    (d.fileNumber === null || typeof d.fileNumber === 'string')
  );
}

/**
 * Where the host handoff stands. `$activeFile` alone cannot express this: null means both
 * "the host has not answered yet" and "the host answered, no file is open", and a screen
 * that cannot function without a file has to tell those apart — otherwise it either renders
 * a half-filled state or waits forever.
 *
 * - `pending` — the request is out, waiting on the host.
 * - `ready` — a file is in context (supplied by the host, or by `?fileId=` in a browser);
 *   `$activeFile` is populated.
 * - `none` — no file is in context: the host answered that none is open (the user closed it, or is
 *   on the home page), or we are in a regular browser with no valid `?fileId=`.
 * - `unavailable` — we are inside the desktop host and it never answered.
 *
 * `none` and `unavailable` are kept apart deliberately: the first is a normal thing for a user to
 * do and wants "open a file", the second is a broken handoff and wants "try again". A regular
 * browser has no host to fail, so it is never `unavailable`.
 */
export type ActiveFileStatus = 'pending' | 'ready' | 'none' | 'unavailable';

export const $activeFileStatus = atom<ActiveFileStatus>('pending');

/**
 * How long to wait for the host's reply before giving up. The host answers in milliseconds
 * when it is listening, so this only elapses when nothing is on the other end.
 */
const HOST_REPLY_TIMEOUT_MS = 5000;

let replyTimer: ReturnType<typeof setTimeout> | undefined;
let initialized = false;

/**
 * Register the host message listener, once. Kept separate from the request below so a retry can
 * establish the listener too: if the host was not present when the app booted, posting a request
 * without a listener would leave the reply nowhere to land.
 */
function ensureBridge(): void {
  if (initialized || !isWebView2Host()) return;
  initialized = true;

  onHostMessage(data => {
    if (!isFileContextMessage(data)) return;
    clearTimeout(replyTimer);
    $activeFile.set(
      data.fileId !== null
        ? {fileId: data.fileId, fileNumber: data.fileNumber}
        : null,
    );
    $activeFileStatus.set(data.fileId !== null ? 'ready' : 'none');
    // Traceability: record which file is associated with this Assistant session.
    console.debug('[activeFile] host file context updated', $activeFile.get());
  });
}

// Undefined until the first read. The URL is read once and kept: the router rewrites the query
// string when tabs change, and a later read would lose a file the user was already working on.
let browserFile: ActiveFile | null | undefined;

/**
 * The file named by `?fileId=` in a regular browser, or null when the id is missing or not a
 * positive integer. Only the id is read: a file number taken from the URL could disagree with the
 * id, and it would then be shown to the user and quoted to the model as the file's name. With no
 * number, the header shows the id instead. Supplying an id grants nothing: every file endpoint
 * re-checks the caller's own ResWare access.
 * Never consulted inside the desktop host, which is authoritative there.
 */
function readBrowserFile(): ActiveFile | null {
  if (browserFile !== undefined) return browserFile;

  const params = new URLSearchParams(window.location.search);
  const rawId = params.get('fileId')?.trim() ?? '';
  const fileId = /^[1-9]\d*$/.test(rawId) ? Number(rawId) : NaN;

  if (!Number.isSafeInteger(fileId)) {
    if (rawId !== '') {
      console.debug('[activeFile] ignoring invalid fileId parameter', rawId);
    }
    browserFile = null;
    return browserFile;
  }

  browserFile = {fileId, fileNumber: null};
  return browserFile;
}

/**
 * The `&fileId=...` suffix that carries the browser file onto an in-app link, or ''
 * inside the desktop host or when the URL named no file. Links built from fixed strings would
 * otherwise drop it from the address bar, and a reload or copied link would lose the file.
 */
export function browserFileQuery(): string {
  const file = isWebView2Host() ? null : readBrowserFile();
  if (!file) return '';
  return `&fileId=${file.fileId}`;
}

/**
 * Ask the host for its current file. Also backs the retry affordance on screens that cannot
 * function without one. Outside the desktop host there is nothing to ask and nothing to fail:
 * the status settles on `ready` when the URL names a file and on `none` otherwise.
 */
export function requestActiveFile(): void {
  ensureBridge();

  if (!isWebView2Host()) {
    const file = readBrowserFile();
    $activeFile.set(file);
    $activeFileStatus.set(file ? 'ready' : 'none');
    return;
  }

  $activeFileStatus.set('pending');
  clearTimeout(replyTimer);
  replyTimer = setTimeout(() => {
    if ($activeFileStatus.get() === 'pending') {
      $activeFileStatus.set('unavailable');
      console.debug('[activeFile] host did not supply a file context in time');
    }
  }, HOST_REPLY_TIMEOUT_MS);

  postToHost({type: REQUEST_FILE_CONTEXT_MESSAGE_TYPE});
}

/**
 * Start mirroring the active ResWare file from the desktop host and request its current value.
 * Safe to call unconditionally: outside the desktop WebView2 host there is nothing to mirror, so
 * `$activeFile` comes from `?fileId=` (or stays null) and the status settles on `ready` or `none`.
 *
 * The listener is registered before the request goes out, since a push sent before that point
 * would be lost — the host waits for this request on load. The listener is only ever registered
 * once; a repeat call just re-asks the host.
 */
export function initActiveFileBridge(): void {
  requestActiveFile();
}
